import { Anchor, Badge, Button, Grid, Group, Table, Text, Title } from '@mantine/core';
import {
  IconCheck,
  IconExternalLink,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlugConnectedX,
  IconX,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isAppUninstalled } from '../helpers/domainSuspension';
import useFetch from '../helpers/useFetch';
import { setDomain as setDomainRedux } from '../store/domainSlice';
import ErrorMessage from './ErrorMessage';
import DomainSuspendResumeConfirmationModal from './Modals/DomainSuspendResumeConfirmationModal';
import Spinner from './Spinner';

const blacklistedPlatforms = ['shopify'];

const AccountSubscriptions = () => {
  const fetch = useFetch();
  const [billingPeriod, setbillingPeriod] = useState();
  const [remainingTrialDays, setRemainingTrialDays] = useState();
  const [error, setError] = useState();
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const dispatch = useDispatch();
  const { account } = useSelector((state) => state.account);
  const { domain } = useSelector((state) => state.domain);

  const fetchURL = `/api/admin/accounts/${account.id}/subscriptions`;

  useEffect(() => {
    fetch(fetchURL)
      .then(({ account, billingPeriod, remainingTrialDays }) => {
        setbillingPeriod(billingPeriod);
        setRemainingTrialDays(remainingTrialDays);
      })
      .catch(setError);
  }, [domain, fetch, fetchURL]);

  const toggleSubscription = (domain) => {
    return fetch(`/api/admin/accounts/${account.id}/domains/${domain.id}`, {
      method: 'PUT',
      body: JSON.stringify({ running: !domain.running }),
    })
      .then(({ domain }) => {
        dispatch(setDomainRedux(domain));
      })
      .catch((error) => {
        setError(error);
      });
  };

  if (account === undefined) {
    return <Spinner />;
  }

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  const activeSubscription = (
    <Text c="green">
      <IconCheck size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Active
    </Text>
  );
  const inactiveSubscription = (
    <Text c="red">
      <IconX size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Inactive
    </Text>
  );
  // A shop that stopped because the merchant removed the app is not the same
  // thing as one we suspended, and only one of them is ours to fix. Reading them
  // both as a flat "Inactive" is what sent support chasing merchants who had
  // simply left.
  const uninstalledSubscription = (
    <Text c="red">
      <IconPlugConnectedX size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} /> App uninstalled
    </Text>
  );

  // 'inactive' rows are plans that were cancelled but kept as history, so they
  // are labelled rather than hidden - a merchant looking at a stopped shop should
  // see what it used to be on. Rendered as elements rather than joined into a
  // string: the previous version interpolated nothing at all and printed the
  // literal text "s.appliedValues.name" once per subscription.
  const subscriptionStatusBadge = (status) => {
    if (status === 'pendingConfirmation') {
      return (
        <Badge variant="default" size="sm" ml={4}>
          pending confirmation
        </Badge>
      );
    }
    if (status === 'inactive') {
      return (
        <Badge variant="light" color="red" size="sm" ml={4}>
          cancelled
        </Badge>
      );
    }
    return null;
  };

  const domainsTableRows =
    account.domains?.length > 0
      ? account.domains.map((domain) => {
          return (
            <Table.Tr key={domain.id}>
              <Table.Td visibleFrom="md">
                <strong>{domain.name}</strong>
              </Table.Td>
              <Table.Td>
                {domain.running
                  ? activeSubscription
                  : isAppUninstalled(domain)
                    ? uninstalledSubscription
                    : inactiveSubscription}
              </Table.Td>
              <Table.Td>
                {domain.domainSubscriptions.length > 0 ? (
                  domain.domainSubscriptions.map((s, i) => (
                    <span key={s.id ?? i}>
                      {i > 0 && ', '}
                      {s.appliedValues?.name}
                      {subscriptionStatusBadge(s.status)}
                    </span>
                  ))
                ) : (
                  <Text>Custom</Text>
                )}
              </Table.Td>
              <Table.Td>
                {parseFloat(domain.totalPriceUSD).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                <Text size="sm" c="dimmed">
                  <Anchor
                    href={`/#/accounts/${domain.accountId}/domains/${domain.id}/settings/domain?tab=usage`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    see breakdown <IconExternalLink size={12} style={{ verticalAlign: 'middle' }} />
                  </Anchor>
                </Text>
              </Table.Td>
              <Table.Td>
                {domain.priceCapUSD
                  ? parseFloat(domain.priceCapUSD).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                  : 'N/A'}
              </Table.Td>
              <Table.Td>
                <Group justify="flex-end">
                  <DomainSuspendResumeConfirmationModal
                    onConfirm={() => toggleSubscription(domain)}
                    show={confirmationOpen === domain.id}
                    running={domain.running}
                    close={() => setConfirmationOpen(undefined)}
                  />
                  {!blacklistedPlatforms.includes(domain.platform) ? (
                    <Group>
                      {domain.running ? (
                        <Button
                          color="red"
                          size="xs"
                          onClick={() => setConfirmationOpen(domain.id)}
                          leftSection={<IconPlayerPause size={14} />}
                        >
                          Cancel Subscription
                        </Button>
                      ) : (
                        <Button
                          color="green"
                          size="xs"
                          onClick={() => setConfirmationOpen(domain.id)}
                          rightSection={<IconPlayerPlay size={14} />}
                        >
                          Resume Subscription
                        </Button>
                      )}
                    </Group>
                  ) : null}
                </Group>
              </Table.Td>
            </Table.Tr>
          );
        })
      : null;

  return (
    <Grid>
      <Grid.Col>
        <Group justify="space-between" mb="md">
          <Title order={3} c="dimmed">
            Billing period:{' '}
            {billingPeriod ? (
              <>
                {new Date(billingPeriod.start).toLocaleDateString()} to{' '}
                {new Date(billingPeriod.end).toLocaleDateString()}
              </>
            ) : (
              <>Unavailable</>
            )}
          </Title>
          {remainingTrialDays > 0 && <Text c="dimmed">{remainingTrialDays} trial days remaining</Text>}
        </Group>
        <Table striped withTableBorder highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th visibleFrom="md">Shop</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Subscriptions</Table.Th>
              <Table.Th>Price (Billing period until now excl. VAT)*</Table.Th>
              <Table.Th>Spending Limit (excl. VAT)</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{domainsTableRows}</Table.Tbody>
        </Table>
        <Text size="sm" c="dimmed">
          * Updated daily.
        </Text>
      </Grid.Col>
    </Grid>
  );
};

export default AccountSubscriptions;
